import { useState, useEffect, useRef, useCallback } from 'react';
import { Section } from '@/types';

interface PlaybackState {
  currentSection: Section | null;
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume: number;
  playbackRate: number;
  trackList: Section[];
  currentTrackIndex: number;
}

const STORAGE_KEYS = {
  VOLUME: 'wisdom:player:volume',
  PLAYBACK_RATE: 'wisdom:player:playbackRate',
};

export const useAudioPlayer = (
  getAudioUrl?: (section: Section) => Promise<string>,
  onTrackListEnded?: () => void
) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isMountedRef = useRef(true);
  const getAudioUrlRef = useRef(getAudioUrl);
  getAudioUrlRef.current = getAudioUrl;
  const onTrackListEndedRef = useRef(onTrackListEnded);
  onTrackListEndedRef.current = onTrackListEnded;

  const [state, setState] = useState<PlaybackState>({
    currentSection: null,
    isPlaying: false,
    progress: 0,
    duration: 0,
    volume: parseFloat(localStorage.getItem(STORAGE_KEYS.VOLUME) || '1'),
    playbackRate: parseFloat(
      localStorage.getItem(STORAGE_KEYS.PLAYBACK_RATE) || '1'
    ),
    trackList: [],
    currentTrackIndex: -1,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  // Load and play a section by fetching its audio URL
  const loadAndPlay = useCallback(async (section: Section) => {
    const audio = audioRef.current;
    if (!audio || !getAudioUrlRef.current || !isMountedRef.current) return;

    try {
      const url = await getAudioUrlRef.current(section);
      if (!isMountedRef.current) return;
      if (!url) {
        console.warn('No audio URL for section:', section.id);
        return;
      }
      audio.src = url;
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.error('Error playing audio:', error);
      });
    } catch (error) {
      console.error('Error fetching audio URL:', error);
    }
  }, []);

  // Initialize audio element and stop playback when component unmounts
  useEffect(() => {
    isMountedRef.current = true;

    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = stateRef.current.volume;
      audioRef.current.playbackRate = stateRef.current.playbackRate;
    }

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setState((prev) => ({
        ...prev,
        progress: audio.currentTime,
      }));
    };

    const handleLoadedMetadata = () => {
      setState((prev) => ({
        ...prev,
        duration: audio.duration,
      }));
    };

    const handleEnded = () => {
      const { currentTrackIndex, trackList } = stateRef.current;
      if (currentTrackIndex < trackList.length - 1) {
        // Next section within current chapter
        const nextSection = trackList[currentTrackIndex + 1];
        setState((prev) => ({
          ...prev,
          currentSection: nextSection,
          currentTrackIndex: currentTrackIndex + 1,
          progress: 0,
        }));
        loadAndPlay(nextSection);
      } else if (onTrackListEndedRef.current) {
        // Last section of chapter — notify parent to advance to next chapter
        onTrackListEndedRef.current();
      } else {
        setState((prev) => ({
          ...prev,
          isPlaying: false,
        }));
      }
    };

    const handlePlay = () => {
      setState((prev) => ({
        ...prev,
        isPlaying: true,
      }));
    };

    const handlePause = () => {
      setState((prev) => ({
        ...prev,
        isPlaying: false,
      }));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      isMountedRef.current = false;
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    };
  }, [loadAndPlay]);

  const play = useCallback(
    async (section: Section, trackList: Section[], audioUrl: string) => {
      const audio = audioRef.current;
      if (!audio || !isMountedRef.current) return;
      const trackIndex = trackList.findIndex((s) => s.id === section.id);

      setState((prev) => ({
        ...prev,
        currentSection: section,
        trackList,
        currentTrackIndex: trackIndex,
        progress: 0,
      }));

      audio.src = audioUrl;
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.error('Error playing audio:', error);
      });
    },
    []
  );

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
  }, []);

  const resume = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !isMountedRef.current) return;
    audio.play().catch((error) => {
      console.error('Error resuming audio:', error);
    });
  }, []);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.min(time, audio.duration);
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    const volume = Math.max(0, Math.min(1, newVolume));
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
    localStorage.setItem(STORAGE_KEYS.VOLUME, String(volume));
    setState((prev) => ({
      ...prev,
      volume,
    }));
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    const validRate = [0.75, 1, 1.25, 1.5, 2].includes(rate) ? rate : 1;
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = validRate;
    }
    localStorage.setItem(STORAGE_KEYS.PLAYBACK_RATE, String(validRate));
    setState((prev) => ({
      ...prev,
      playbackRate: validRate,
    }));
  }, []);

  const next = useCallback(() => {
    const { currentTrackIndex, trackList } = stateRef.current;
    if (currentTrackIndex < trackList.length - 1) {
      const nextSection = trackList[currentTrackIndex + 1];
      setState((prev) => ({
        ...prev,
        currentSection: nextSection,
        currentTrackIndex: currentTrackIndex + 1,
        progress: 0,
      }));
      loadAndPlay(nextSection);
    }
  }, [loadAndPlay]);

  const previous = useCallback(() => {
    const { currentTrackIndex, trackList } = stateRef.current;
    if (currentTrackIndex > 0) {
      const prevSection = trackList[currentTrackIndex - 1];
      setState((prev) => ({
        ...prev,
        currentSection: prevSection,
        currentTrackIndex: currentTrackIndex - 1,
        progress: 0,
      }));
      loadAndPlay(prevSection);
    }
  }, [loadAndPlay]);

  const jumpToTrack = useCallback((index: number) => {
    const { trackList } = stateRef.current;
    if (index >= 0 && index < trackList.length) {
      const section = trackList[index];
      setState((prev) => ({
        ...prev,
        currentSection: section,
        currentTrackIndex: index,
        progress: 0,
      }));
      loadAndPlay(section);
    }
  }, [loadAndPlay]);

  return {
    ...state,
    play,
    pause,
    resume,
    seek,
    setVolume,
    setPlaybackRate,
    next,
    previous,
    jumpToTrack,
  };
};
